// The wire of the consistency rules engine (SEP-243 section 39).
//
// Endpoints under `/api/consistency/*`:
//   GET   /api/consistency/rule-set                     the rule book, as Compliance sees it
//   POST  /api/consistency/rule-set                     publish a new version
//   PATCH /api/consistency/rule-set/:ruleSetId/rules/:ruleId   set one rule
//   GET   /api/consistency/cases/:caseId/flags          the raised flags of one case
//   POST  /api/consistency/cases/:caseId/evaluate       run the rules and reconcile
//   POST  /api/consistency/flags/:id/confirm            the client confirmed both answers
//   POST  /api/consistency/flags/:id/resolve            the IM recorded the resolution note
//
// THE EVALUATE ENDPOINT TAKES A CASE ID AND NOTHING ELSE. The controller reads
// the case through `ctx.read`, builds the facts, runs the pure engine and
// dispatches the plan it computed. No request body carries a flag, so no caller
// can raise one that the rules did not raise.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

const errorBody = z.object({ error: z.string(), code: z.string().optional() });

/** The tier codes of the master specification. */
export const consistencyTierSchema = z.enum(['H', 'S', 'S->I', 'I', 'D']);

/** One named threshold of one rule, as the Comply surface shows it. */
export const consistencyThresholdSchema = z.object({
    key: z.string(),
    label: z.string(),
    unit: z.enum(['percent', 'years', 'months', 'gbp', 'count', 'ratio']),
    /** The value the rule set holds now. */
    value: z.number(),
    /** The value the platform ships. */
    defaultValue: z.number(),
    /**
     * False when the firm has not agreed the number (decision D-09). The
     * surface prints the unconfirmed state, so nobody reads a placeholder as a
     * firm decision.
     */
    confirmed: z.boolean(),
});
export type ConsistencyThresholdDto = z.infer<typeof consistencyThresholdSchema>;

/** One rule, with the settings that apply now. */
export const consistencyRuleRowSchema = z.object({
    ruleId: z.string(),
    family: z.string(),
    trigger: z.string(),
    message: z.string(),
    /** The tier the specification gives. */
    defaultTier: consistencyTierSchema,
    /** The tier that applies now. */
    tier: consistencyTierSchema,
    enabled: z.boolean(),
    confidence: z.enum(['verbatim', 'reconstructed', 'uncertain']),
    /** The reading the platform implements, and the doubt where there is one. */
    statement: z.string(),
    thresholds: z.array(consistencyThresholdSchema),
});
export type ConsistencyRuleRowDto = z.infer<typeof consistencyRuleRowSchema>;

export const consistencyRuleSetResponseSchema = z.object({
    /** Null when the firm has published no rule set, and the shipped one applies. */
    ruleSetId: z.string().nullable(),
    version: z.string(),
    activeFrom: z.string(),
    rules: z.array(consistencyRuleRowSchema),
});
export type ConsistencyRuleSetResponse = z.infer<typeof consistencyRuleSetResponseSchema>;

export const publishConsistencyRuleSetRequestSchema = z.object({
    version: z.string().min(1).max(200),
    activeFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    note: z.string().max(4000).nullable().optional(),
});

export const setConsistencyRuleRequestSchema = z.object({
    enabled: z.boolean(),
    tier: consistencyTierSchema.nullable(),
    thresholds: z.record(z.string(), z.number().finite()),
    confirmedThresholds: z.array(z.string()),
});

/** One raised flag, as a case panel and the Comply surface show it. */
export const consistencyFlagSchema = z.object({
    id: z.string(),
    caseId: z.string(),
    clientId: z.string(),
    ruleId: z.string(),
    family: z.string(),
    tierAtRaise: consistencyTierSchema,
    /** The tier that applies now. A client confirmation converts a soft flag. */
    effectiveTier: consistencyTierSchema,
    ruleSetVersion: z.string(),
    message: z.string(),
    trigger: z.string(),
    values: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])),
    /**
     * The numbers the rule read at the raise, by threshold key. Compliance
     * edits the active rule set in place, so the version label alone does not
     * say which numbers applied.
     */
    thresholdsAtRaise: z.record(z.string(), z.number()),
    occurrence: z.number().int(),
    status: z.enum(['open', 'confirmed_by_client', 'resolved', 'withdrawn']),
    raisedAt: z.string(),
    confirmedByClientAt: z.string().nullable(),
    confirmationNote: z.string().nullable(),
    resolvedAt: z.string().nullable(),
    resolutionNote: z.string().nullable(),
    resolutionKind: z.enum(['rule_passes', 'im_note', 'document_gate']).nullable(),
    /** Null when the flag holds no gate. */
    approvalBlockReason: z
        .enum([
            'hard_answer_not_corrected',
            'client_has_not_answered',
            'im_note_missing',
            'documents_not_on_file',
        ])
        .nullable(),
});
export type ConsistencyFlagDto = z.infer<typeof consistencyFlagSchema>;

export const consistencyFlagsResponseSchema = z.object({
    caseId: z.string(),
    /** Every flag, live and cleared. The register is append-only. */
    flags: z.array(consistencyFlagSchema),
    /** The ids of the flags that hold the IM approval gate. */
    approvalBlockerIds: z.array(z.string()),
    /** The ids of the flags that hold the client submission gate. */
    submissionBlockerIds: z.array(z.string()),
});
export type ConsistencyFlagsResponse = z.infer<typeof consistencyFlagsResponseSchema>;

export const evaluateConsistencyResponseSchema = z.object({
    caseId: z.string(),
    ruleSetVersion: z.string(),
    evaluatedAt: z.string(),
    raised: z.number().int(),
    resolved: z.number().int(),
    withdrawn: z.number().int(),
    /** The ids of the rules that fire against the case now. */
    firedRuleIds: z.array(z.string()),
    /** The rules the caller could not evaluate, because an answer is missing. */
    notEvaluatedRuleIds: z.array(z.string()),
});

export const confirmConsistencyFlagRequestSchema = z.object({
    confirmationNote: z.string().min(1).max(4000),
});

export const resolveConsistencyFlagRequestSchema = z.object({
    resolutionNote: z.string().min(1).max(4000),
});

/**
 * The clearance of a document gate (SEP-243 item 5).
 *
 * The note alone never clears a `D` flag, so the request carries the
 * `ReceivedDocumentRequirement` rows that satisfy it. At least one: a gate
 * cleared against no document is a resolution note wearing a different name.
 */
export const clearConsistencyDocumentGateRequestSchema = z.object({
    evidenceDocumentIds: z.array(z.string().min(1)).min(1),
    resolutionNote: z.string().min(1).max(4000),
});

export const consistencyContract = c.router(
    {
        ruleSet: {
            method: 'GET',
            path: '/consistency/rule-set',
            responses: { 200: consistencyRuleSetResponseSchema, 403: errorBody },
            summary:
                'The 49 consistency rules with the tier, the on-off state and the thresholds that apply now (SEP-243 section 39)',
        },
        publishRuleSet: {
            method: 'POST',
            path: '/consistency/rule-set',
            body: publishConsistencyRuleSetRequestSchema,
            responses: { 200: z.object({ id: z.string() }), 400: errorBody, 403: errorBody },
            summary:
                'Open a new version of the rule set. It copies the settings of the version it follows, and it takes effect on its activeFrom date',
        },
        setRule: {
            method: 'PATCH',
            path: '/consistency/rule-set/:ruleSetId/rules/:ruleId',
            body: setConsistencyRuleRequestSchema,
            responses: {
                200: z.object({ id: z.string() }),
                400: errorBody,
                // 403: Compliance owns the rule book, and nobody else writes it.
                403: errorBody,
                404: errorBody,
            },
            summary: 'Set the on-off state, the tier and the thresholds of one rule',
        },
        caseFlags: {
            method: 'GET',
            path: '/consistency/cases/:caseId/flags',
            responses: { 200: consistencyFlagsResponseSchema, 403: errorBody, 404: errorBody },
            summary: 'Every consistency flag of one onboarding case, live and cleared',
        },
        evaluateCase: {
            method: 'POST',
            path: '/consistency/cases/:caseId/evaluate',
            body: z.object({}),
            responses: { 200: evaluateConsistencyResponseSchema, 403: errorBody, 404: errorBody },
            summary:
                'Run the rules over one case and reconcile the flag register. It raises, resolves and withdraws, and it deletes nothing',
        },
        confirmFlag: {
            method: 'POST',
            path: '/consistency/flags/:id/confirm',
            body: confirmConsistencyFlagRequestSchema,
            responses: {
                200: z.object({ id: z.string() }),
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary:
                'Record that the client confirmed both answers of a soft flag. The flag then converts to an IM flag',
        },
        resolveFlag: {
            method: 'POST',
            path: '/consistency/flags/:id/resolve',
            body: resolveConsistencyFlagRequestSchema,
            responses: {
                200: z.object({ id: z.string() }),
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary:
                'The investment manager records the resolution note, and the flag stops holding the approval gate',
        },
        clearDocumentGate: {
            method: 'POST',
            path: '/consistency/flags/:id/clear-document-gate',
            body: clearConsistencyDocumentGateRequestSchema,
            responses: {
                200: z.object({ id: z.string() }),
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary:
                'Compliance records the certified documents that satisfy a document gate, and the flag stops holding the approval gate',
        },
    },
    { pathPrefix: '/api', strictStatusCodes: false },
);
